import axios from "axios";
import { proporcionDe, fotoDeArticulo } from "./desgloseOC";

// Desglose de un pedido para su PDF y su Excel: un renglon por modelo (articulo +
// renglon del pedido, asi el mismo modelo dos veces sale dos veces) con sus
// colores y sus tallas, las piezas de cada uno y su proporcion, y debajo las
// variantes. Mismo criterio que el PDF de la orden de compra.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

// Orden de tallas: las conocidas por su escala, los numeros de menor a mayor y
// el resto al final en el orden en que llegaron.
const ESCALA = ["XXS", "XS", "XCH", "CH", "S", "M", "G", "L", "EG", "XL", "XG", "XXL", "2XG", "XXG", "3XG", "4XG", "5XG", "UNI", "UNITALLA"];
export const rangoTalla = (t) => {
  const x = String(t || "").trim().toUpperCase();
  const i = ESCALA.indexOf(x);
  if (i >= 0) return i;
  const n = parseFloat(x);
  return Number.isFinite(n) ? 100 + n : 1000;
};
export const ordenTallas = (lista) => lista.map((t, i) => ({ t, i })).sort((a, b) => (rangoTalla(a.t) - rangoTalla(b.t)) || (a.i - b.i)).map((x) => x.t);

// La imagen de "sin imagen" como dataURL, una sola vez
let sinImagen;
async function imagenVacia() {
  if (sinImagen !== undefined) return sinImagen;
  try {
    const blob = await fetch(process.env.PUBLIC_URL + "/sinImagen.png").then((x) => (x.ok ? x.blob() : null));
    sinImagen = blob ? await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    }) : null;
  } catch (e) { sinImagen = null; }
  return sinImagen;
}

export async function desglosePedido(idPedido, { conFoto = true } = {}) {
  const r = await axios.get(`${process.env.REACT_APP_URL_ARTICULOS_PEDIDO}/surtir2/${idPedido}`, { headers: headers() });
  const lineas = (r.data || []).filter((a) => a.articulos?.[0]);
  const porModelo = new Map();
  lineas.forEach((a) => {
    const art = a.articulos[0];
    const renglon = a.renglon || 1;
    const k = `${art._id}|${renglon}`;
    if (!porModelo.has(k)) {
      porModelo.set(k, {
        articulos: String(art._id), renglon, codigo: art.codigo || "", nombre: art.nombre || "",
        precio: Number(a.precio) || 0, piezas: 0, surtido: 0, pendiente: 0, total: 0, foto: null, variantes: [],
      });
    }
    const x = porModelo.get(k);
    const cantidad = Number(a.cantidad) || 0;
    const surtido = Number(a.surtido) || 0;
    const pendiente = Math.max(0, Number(a.pendiente_surtir) || 0);
    const precio = Number(a.precio) || 0;
    x.piezas += cantidad;
    x.surtido += surtido;
    x.pendiente += pendiente;
    x.total += Number(a.total) || cantidad * precio;
    x.variantes.push({
      idColor: a.colores?.[0]?._id || "",
      color: a.colores?.[0]?.name || "Sin color",
      talla: a.tallas?.[0]?.name || "Sin talla",
      cantidad, surtido, pendiente, precio, total: Number(a.total) || cantidad * precio,
    });
  });

  const modelos = [...porModelo.values()].sort((a, b) => (a.renglon - b.renglon) || (a.codigo > b.codigo ? 1 : -1));
  modelos.forEach((m) => {
    const porColor = {}, porTalla = {};
    m.variantes.forEach((v) => {
      porColor[v.color] = (porColor[v.color] || 0) + v.cantidad;
      porTalla[v.talla] = (porTalla[v.talla] || 0) + v.cantidad;
    });
    m.colores = Object.keys(porColor);
    m.tallas = ordenTallas(Object.keys(porTalla));
    m.variantes.sort((a, b) => (a.color > b.color ? 1 : a.color < b.color ? -1 : rangoTalla(a.talla) - rangoTalla(b.talla)));
    m.proporcionColores = proporcionDe(m.colores.map((c) => porColor[c]));
    m.proporcionTallas = proporcionDe(m.tallas.map((t) => porTalla[t]));
    // columnas alineadas: un color (o talla) por linea con sus piezas
    m.colColores = m.colores.map((c) => `${c}  ${num(porColor[c])}`).join("\n");
    m.colTallas = m.tallas.map((t) => `${t}  ${num(porTalla[t])}`).join("\n");
    // de una linea, para el Excel
    m.desgloseColores = m.colores.map((c) => `${c} ${num(porColor[c])}`).join(" | ");
    m.desgloseTallas = m.tallas.map((t) => `${t} ${num(porTalla[t])}`).join(" | ");
    // el mismo modelo en dos renglones del pedido: se distingue con su numero
    m.repetido = modelos.filter((o) => o.articulos === m.articulos).length > 1;
  });

  // Las fotos se bajan ANTES de armar el documento: si se mandan a jsPDF sin
  // esperar, addImage truena o sale en blanco.
  if (conFoto) {
    const unicas = [...new Set(modelos.map((m) => m.articulos))];
    const vacia = await imagenVacia();
    const fotos = (await Promise.all(unicas.map((id) => fotoDeArticulo(id)))).map((f) => f || vacia);
    const porId = Object.fromEntries(unicas.map((id, i) => [id, fotos[i]]));
    modelos.forEach((m) => { m.foto = porId[m.articulos]; });
  }
  return modelos;
}

export default desglosePedido;
